"use client";

import React, { useState, useEffect, useRef, useCallback } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { useAuth } from "@/src/contexts/AuthContext";
import { useCart } from "@/src/contexts/CartContext";
import { useTenant } from "@/src/contexts/TenantContext";
import { LoginStep } from "@/src/app/shop/checkout/components/LoginStep";
import { getAddresses } from "@/src/services/address.service";
import { searchProducts, type SearchResult } from "@/src/services/product.service";
import type { Address } from "@/src/types/address";

export const Header: React.FC = () => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { storeName, logoUrl, outletId: tenantOutletId, prefix: tenantPrefix } = useTenant();

  // Fall back to URL params when TenantContext hasn't been populated yet
  // (e.g. on product, profile, orders pages where ShopClient isn't mounted)
  const outletId = tenantOutletId || searchParams.get("outletId") || "";
  const prefix = tenantPrefix || searchParams.get("prefix") || "";
  const { isAuthenticated, name, phoneNumber, logout, userId, token } = useAuth();
  const { getItemCount } = useCart();
  const [showLogin, setShowLogin] = useState(false);
  const [showAddressBox, setShowAddressBox] = useState(false);
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [selectedAddress, setSelectedAddress] = useState<Address | null>(null);
  const [loadingAddresses, setLoadingAddresses] = useState(false);
  const addressBoxRef = useRef<HTMLDivElement>(null);

  const cartCount = getItemCount();
  const displayName = name || (phoneNumber ? `•••• ${phoneNumber.slice(-4)}` : null);

  // ── Search ────────────────────────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleSearchChange = useCallback((val: string) => {
    setSearchQuery(val);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (!val.trim() || !outletId) {
      setSearchResults([]);
      setSearchOpen(false);
      return;
    }
    searchTimerRef.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const results = await searchProducts(val.trim(), outletId);
        setSearchResults(results);
        setSearchOpen(true);
      } catch {
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300);
  }, [outletId]);

  const handleResultClick = (result: SearchResult) => {
    setSearchOpen(false);
    setSearchQuery("");
    setSearchResults([]);
    router.push(`/shop/product/${result.id}?prefix=${prefix}&outletId=${outletId}`);
  };

  // Close address box on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (addressBoxRef.current && !addressBoxRef.current.contains(e.target as Node)) {
        setShowAddressBox(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleOpenAddressBox = async () => {
    setShowAddressBox((prev) => !prev);
    if (!showAddressBox && userId && token && addresses.length === 0) {
      setLoadingAddresses(true);
      try {
        const list = await getAddresses(token, userId);
        setAddresses(list);
        if (list.length > 0 && !selectedAddress) {
          // Restore previously chosen address, or fall back to first
          const storedId = localStorage.getItem("selected_address_id");
          const restored = storedId ? list.find((a) => a.id === Number(storedId)) : null;
          const toSelect = restored ?? list[0];
          setSelectedAddress(toSelect);
          localStorage.setItem("selected_address_id", String(toSelect.id));
          if (toSelect.coordinates) {
            localStorage.setItem("selected_address_coords", JSON.stringify(toSelect.coordinates));
            window.dispatchEvent(new Event("address_changed"));
          }
        }
      } catch { /* non-fatal */ }
      setLoadingAddresses(false);
    }
  };

  return (
    <>
      <header
        className="sticky top-0 z-50 border-b"
        style={{
          background: "rgba(232, 234, 226, 0.88)",
          backdropFilter: "saturate(180%) blur(20px)",
          WebkitBackdropFilter: "saturate(180%) blur(20px)",
          borderColor: "var(--border)",
          height: "64px",
        }}
      >
        <div className="gl-container flex h-full items-center gap-3">
          {/* Brand logo */}
          <button
            onClick={() => router.push(`/shop?prefix=${prefix}`)}
            className="flex shrink-0 items-center rounded-xl px-3 py-1.5"
            style={{ background: "#24242C" }}
          >
            <img
              src="https://grablocal-assets-gsdxahdwbkcjhgah.z03.azurefd.net/productimages/grablocal_logo_white_nobg%20(1).png"
              alt="GrabLocal"
              style={{ height: 28, width: "auto", flexShrink: 0, objectFit: "contain" }}
            />
          </button>

          {/* Search bar — hidden on mobile */}
          <div className="relative hidden flex-1 md:block" ref={searchRef}>
            <div
              className="flex items-center gap-3 rounded-[10px] border px-3.5 py-2.5"
              style={{
                background: "var(--surface)",
                borderColor: searchOpen ? "#E4604E" : "var(--border)",
                boxShadow: searchOpen ? "0 0 0 3px rgba(228,96,78,0.12)" : "none",
                transition: "border-color 160ms, box-shadow 160ms",
              }}
            >
              {searchLoading ? (
                <svg className="animate-spin" width="16" height="16" viewBox="0 0 24 24" fill="none"
                  stroke="#E4604E" strokeWidth="2" strokeLinecap="round" style={{ flexShrink: 0 }}>
                  <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                </svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                  strokeWidth="2" strokeLinecap="round" style={{ color: "var(--techno-black-50)", flexShrink: 0 }}>
                  <circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" />
                </svg>
              )}
              <input
                type="text"
                value={searchQuery}
                onChange={e => handleSearchChange(e.target.value)}
                onFocus={() => searchResults.length > 0 && setSearchOpen(true)}
                placeholder={`Search ${storeName || "GrabLocal"}…`}
                className="min-w-0 flex-1 border-none bg-transparent outline-none"
                style={{ fontSize: 14, color: "var(--techno-black)" }}
              />
              {searchQuery ? (
                <button onClick={() => { setSearchQuery(""); setSearchResults([]); setSearchOpen(false); }}
                  style={{ color: "var(--techno-black-50)", flexShrink: 0 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              ) : (
                <span className="hidden rounded border px-1.5 py-0.5 text-[11px] font-semibold lg:inline"
                  style={{ color: "var(--techno-black-50)", borderColor: "var(--border)", flexShrink: 0 }}>
                  ⌘ K
                </span>
              )}
            </div>

            {/* Results dropdown */}
            {searchOpen && searchResults.length > 0 && (
              <div
                className="absolute left-0 right-0 top-[calc(100%+6px)] z-50 overflow-hidden rounded-2xl"
                style={{
                  background: "var(--surface)",
                  border: "1px solid var(--border-soft)",
                  boxShadow: "0 16px 48px rgba(0,0,0,0.12)",
                }}
              >
                <p className="px-4 py-2.5 text-[11px] font-semibold uppercase tracking-widest"
                  style={{ color: "var(--techno-black-50)", borderBottom: "1px solid var(--border-soft)" }}>
                  {searchResults.length} result{searchResults.length !== 1 ? "s" : ""}
                </p>
                <ul className="max-h-[360px] overflow-y-auto">
                  {searchResults.map((r) => {
                    const mrp = parseFloat(r.mrpPrice || "0");
                    const price = parseFloat(r.sellingPrice || "0");
                    const disc = mrp > price && mrp > 0 ? Math.round(((mrp - price) / mrp) * 100) : 0;
                    const hasImage = r.thumbnail?.startsWith("http");
                    return (
                      <li key={r.id}>
                        <button
                          onClick={() => handleResultClick(r)}
                          className="flex w-full items-center gap-3 px-4 py-3 text-left transition"
                          style={{ borderBottom: "1px solid var(--border-soft)" }}
                          onMouseEnter={e => (e.currentTarget.style.background = "var(--surface-2)")}
                          onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
                        >
                          {/* Thumbnail */}
                          <div className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-xl"
                            style={{ background: "linear-gradient(135deg,#F4F1EA,#ECE7DC)" }}>
                            {hasImage
                              ? <img src={r.thumbnail} alt={r.name} className="h-full w-full object-cover" />
                              : <span className="text-[11px] font-bold" style={{ color: "rgba(53,73,48,0.3)" }}>
                                  {r.name.slice(0, 2).toUpperCase()}
                                </span>}
                          </div>
                          {/* Info */}
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-[13.5px] font-semibold"
                              style={{ color: "var(--techno-black)" }}>{r.name}</p>
                            <div className="mt-0.5 flex items-center gap-2">
                              {price > 0 && (
                                <span className="text-[12px] font-bold" style={{ color: "#E4604E" }}>
                                  ₹{price}
                                </span>
                              )}
                              {mrp > price && (
                                <span className="text-[11px] line-through" style={{ color: "var(--techno-black-50)" }}>
                                  ₹{mrp}
                                </span>
                              )}
                              {disc >= 5 && (
                                <span className="rounded px-1 text-[10px] font-bold"
                                  style={{ background: "rgba(228,96,78,0.1)", color: "#E4604E" }}>
                                  {disc}% off
                                </span>
                              )}
                            </div>
                          </div>
                          {/* Arrow */}
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                            style={{ color: "var(--techno-black-50)", flexShrink: 0 }}>
                            <path d="M5 12h14M13 5l7 7-7 7" />
                          </svg>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            {/* No results */}
            {searchOpen && !searchLoading && searchQuery.trim() && searchResults.length === 0 && (
              <div className="absolute left-0 right-0 top-[calc(100%+6px)] z-50 rounded-2xl px-4 py-5 text-center"
                style={{
                  background: "var(--surface)",
                  border: "1px solid var(--border-soft)",
                  boxShadow: "0 16px 48px rgba(0,0,0,0.12)",
                }}>
                <p className="text-sm" style={{ color: "var(--techno-black-50)" }}>
                  No products found for &ldquo;{searchQuery}&rdquo;
                </p>
              </div>
            )}
          </div>

          {/* Right actions */}
          <div className="ml-auto flex items-center gap-1">
            {/* Location chip — shown only when logged in, desktop only */}
            {isAuthenticated && (
              <div className="relative hidden lg:block" ref={addressBoxRef}>
                <button
                  onClick={handleOpenAddressBox}
                  className="flex items-center gap-1.5 rounded-lg px-3 py-2 text-[13px] font-medium transition"
                  style={{ color: "var(--techno-black-70)" }}
                  onMouseEnter={e => (e.currentTarget.style.background = "var(--techno-black-10)")}
                  onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style={{ color: "var(--rising-sun)", flexShrink: 0 }}>
                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5a2.5 2.5 0 110-5 2.5 2.5 0 010 5z"/>
                  </svg>
                  <span>
                    Deliver to{" "}
                    <strong style={{ color: "var(--techno-black)", fontWeight: 600 }}>
                      {selectedAddress ? (selectedAddress.sublocality || selectedAddress.city || "your area") : "your area"}
                    </strong>
                  </span>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" style={{ opacity: 0.4, transform: showAddressBox ? "rotate(180deg)" : "none", transition: "transform 200ms" }}>
                    <polyline points="6 9 12 15 18 9"/>
                  </svg>
                </button>

                {/* Address dropdown */}
                {showAddressBox && (
                  <div
                    className="absolute left-0 top-[calc(100%+8px)] z-50 w-[320px] rounded-2xl bg-white shadow-xl border border-[#E5E2DA] overflow-hidden"
                    style={{ animation: "fadeUp 150ms ease" }}
                  >
                    <div className="px-4 py-3 border-b border-[#F0EDE6]">
                      <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#A8A8A3]">Deliver to</p>
                    </div>

                    {loadingAddresses ? (
                      <div className="flex items-center justify-center py-8 gap-2 text-sm text-[#8A8A85]">
                        <span className="h-4 w-4 animate-spin rounded-full border-2 border-[#E4604E] border-t-transparent" />
                        Loading addresses…
                      </div>
                    ) : addresses.length === 0 ? (
                      <div className="px-4 py-6 text-center text-sm text-[#8A8A85]">
                        No saved addresses yet.
                      </div>
                    ) : (
                      <ul className="max-h-[260px] overflow-y-auto divide-y divide-[#F5F3EE]">
                        {addresses.map((addr) => {
                          const isSelected = selectedAddress?.id === addr.id;
                          return (
                            <li key={addr.id}>
                              <button
                                onClick={() => {
                                  setSelectedAddress(addr);
                                  localStorage.setItem("selected_address_id", String(addr.id));
                                  if (addr.coordinates) {
                                    localStorage.setItem("selected_address_coords", JSON.stringify(addr.coordinates));
                                    window.dispatchEvent(new Event("address_changed"));
                                  }
                                  setShowAddressBox(false);
                                }}
                                className="flex w-full items-start gap-3 px-4 py-3 text-left transition hover:bg-[#FBF9F6]"
                              >
                                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition"
                                  style={{ borderColor: isSelected ? "var(--rising-sun)" : "#D0CCC4", background: isSelected ? "var(--rising-sun)" : "transparent" }}>
                                  {isSelected && <span className="h-2 w-2 rounded-full bg-white" />}
                                </span>
                                <div className="min-w-0 flex-1">
                                  <p className="text-[12px] font-bold capitalize text-[#24242C]">
                                    {addr.tag || "Other"}{addr.sublocality ? ` · ${addr.sublocality}` : ""}
                                  </p>
                                  <p className="mt-0.5 truncate text-[11px] text-[#8A8A85]">
                                    {[addr.houseNo, addr.addressLine1, addr.city].filter(Boolean).join(", ")}
                                  </p>
                                </div>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    )}

                    <div className="border-t border-[#F0EDE6] px-4 py-3">
                      <button
                        onClick={() => { setShowAddressBox(false); router.push(`/shop/checkout?prefix=${prefix}`); }}
                        className="flex w-full items-center justify-center gap-1.5 rounded-xl bg-[#E4604E] py-2.5 text-[12px] font-bold text-white transition hover:bg-[#C84535]"
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                        </svg>
                        Add new address
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
{/* Store name chip for authenticated users */}
            {isAuthenticated ? (
              <>
                <button
                  onClick={() => router.push(`/shop/profile?prefix=${prefix}`)}
                  className="flex items-center gap-2 rounded-lg px-3 py-2 text-[13px] font-medium transition"
                  style={{ color: "var(--techno-black-70)" }}
                  onMouseEnter={e => (e.currentTarget.style.background = "var(--techno-black-10)")}
                  onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                    <circle cx="12" cy="8" r="4" /><path d="M4 21c0-4 4-7 8-7s8 3 8 7" />
                  </svg>
                  <span className="hidden sm:inline">{displayName ?? "My Account"}</span>
                </button>
                <button
                  onClick={logout}
                  className="flex h-10 w-10 items-center justify-center rounded-lg transition"
                  title="Logout"
                  style={{ color: "var(--techno-black-70)" }}
                  onMouseEnter={e => (e.currentTarget.style.background = "var(--techno-black-10)")}
                  onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                    <polyline points="16 17 21 12 16 7" />
                    <line x1="21" y1="12" x2="9" y2="12" />
                  </svg>
                </button>
              </>
            ) : (
              <button
                onClick={() => setShowLogin(true)}
                className="flex h-10 w-10 items-center justify-center rounded-lg transition"
                title="Login"
                style={{ color: "var(--techno-black-70)" }}
                onMouseEnter={e => (e.currentTarget.style.background = "var(--techno-black-10)")}
                onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                  <circle cx="12" cy="8" r="4" /><path d="M4 21c0-4 4-7 8-7s8 3 8 7" />
                </svg>
              </button>
            )}

            {/* Cart icon with badge */}
            <button
              onClick={() => router.push(`/shop/checkout${prefix ? `?prefix=${prefix}` : ""}`)}
              className="relative flex h-10 w-10 items-center justify-center rounded-lg transition"
              style={{ color: "var(--techno-black-70)" }}
              onMouseEnter={e => (e.currentTarget.style.background = "var(--techno-black-10)")}
              onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" />
                <path d="M3 6h18" /><path d="M16 10a4 4 0 01-8 0" />
              </svg>
              {cartCount > 0 && (
                <span
                  className="absolute right-1 top-1 flex min-w-[18px] items-center justify-center rounded-full px-1 text-[10.5px] font-bold text-white"
                  style={{
                    height: 18,
                    background: "var(--rising-sun)",
                    border: "2px solid var(--rev-white)",
                  }}
                >
                  {cartCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </header>

      {showLogin && (
        <LoginStep
          outletId={outletId}
          onComplete={() => setShowLogin(false)}
          onClose={() => setShowLogin(false)}
        />
      )}
    </>
  );
};
